import * as SQLite from 'expo-sqlite'
import { SQLiteDatabase, SQLiteRunResult } from 'expo-sqlite'
import DatabaseSingleton from '../DatabaseSingleton';  

type SQLiteBindValue = string | number | null | Uint8Array;
type SQLiteBindParams = SQLiteBindValue[] | { [key: string]: SQLiteBindValue };

class DatabaseAdapter {
    private static instance: SQLite.SQLiteDatabase;

    private constructor() {}

    public static async initialiceDatabase(): Promise<void> {
        if (!DatabaseAdapter.instance) {
            // ✅ Usar getRealDatabase en lugar de getInstance
            const { getRealDatabase } = await import('../DatabaseSingleton')
            DatabaseAdapter.instance = await getRealDatabase()
        }
    }

    /**
     * @description Este metodo es para ejecutar queries que devuelven algo
     */
    public static async getAll(query: string, params: SQLiteBindParams = []): Promise<unknown[]> {
        await DatabaseAdapter.initialiceDatabase();
        const statement = await DatabaseAdapter.instance.prepareAsync(query);
        try {
            const result = await statement.executeAsync(params);
            const rows = await result.getAllAsync();
            return rows;
        } finally {
            await DatabaseAdapter.finalizeStatement(statement)
        }
    }

    /**
     * @description Este metodo es para ejecutar queries que devuelven un solo resultado
     */
    public static async getFirst(query: string, params: SQLiteBindParams = []): Promise<unknown> {
        await DatabaseAdapter.initialiceDatabase();
        const statement = await DatabaseAdapter.instance.prepareAsync(query);
        try {
            const result = await statement.executeAsync(params);
            const firstRow = await result.getFirstAsync();
            return firstRow;
        } finally {
            await DatabaseAdapter.finalizeStatement(statement)
        }
    }

    /**
     * @description Este metodo es para ejecutar queries que devuelven algo, la mas usada
     */
    public static async run(query: string, params: SQLiteBindParams = []): Promise<SQLiteRunResult> {
        await DatabaseAdapter.initialiceDatabase();
        const statement = await DatabaseAdapter.instance.prepareAsync(query);
        try {
            const result = await statement.executeAsync(params);
            return result;
        } finally {
            await DatabaseAdapter.finalizeStatement(statement)
        }
    }

    /**
     * Try to finalize a prepared statement with retries when the DB is busy/locked.
     */
    private static async finalizeStatement(statement: any): Promise<void> {
        if (!statement || typeof statement.finalizeAsync !== 'function') return;

        const maxRetries = 5;
        let attempt = 0;
        while (true) {
            try {
                await statement.finalizeAsync();
                return;
            } catch (err: any) {
                const msg = err && err.message ? String(err.message).toLowerCase() : '';
                const isLocked = msg.includes('database is locked') || msg.includes('error code 5') || err?.code === 5;
                attempt++;
                if (isLocked && attempt <= maxRetries) {
                    // small backoff
                    const wait = 30 * attempt;
                    await new Promise((res) => setTimeout(res, wait));
                    continue;
                }

                // If not a lock error or retries exhausted, rethrow so callers can handle it
                console.error('[DatabaseAdapter] finalizeAsync failed:', err);
                throw err;
            }
        }
    }

    /**
     * @description Este metodo es para ejecutar queries que no devuelven nada muy util para inserts masivos
     */
    public static async exec(query: string): Promise<void> {
        await DatabaseAdapter.initialiceDatabase();
        await DatabaseAdapter.instance.execAsync(query);
    }
}

export default DatabaseAdapter;


/* 
    Example of usage .run() method
    const result = await db.runAsync('INSERT INTO test (value, intValue) VALUES (?, ?)', 'aaa', 100);
    await db.runAsync('UPDATE test SET intValue = ? WHERE value = ?', 999, 'aaa'); // Binding unnamed parameters from variadic arguments
    await db.runAsync('UPDATE test SET intValue = ? WHERE value = ?', [999, 'aaa']); // Binding unnamed parameters from array
    await db.runAsync('DELETE FROM test WHERE value = $value', { $value: 'aaa' }); // Binding named parameters from object
*/

/* 
    Example of usage .getFirst() method
    const firstRow = await db.getFirstAsync('SELECT * FROM test');
    console.log(firstRow.id, firstRow.value, firstRow.intValue)
*/

/* 
    Example of usage .getAll() method
    const allRows = await db.getAllAsync('SELECT * FROM test');
    for (const row of allRows) {
        console.log(row.id, row.value, row.intValue);
    }
*/

/* 
    Example of usage .exec() method
    await db.execAsync(`
    PRAGMA journal_mode = WAL;
    CREATE TABLE IF NOT EXISTS test (id INTEGER PRIMARY KEY NOT NULL, value TEXT NOT NULL, intValue INTEGER);
    INSERT INTO test (value, intValue) VALUES ('test1', 123);
    INSERT INTO test (value, intValue) VALUES ('test2', 456);
    INSERT INTO test (value, intValue) VALUES ('test3', 789);
    `);
*/
